/* eslint-disable no-console */
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { subscribe } from 'alfa';
import { Button, message, Tabs, Typography } from 'antd';
import { withTranslation } from 'react-i18next';
import XLSX from 'xlsx';

import { LoadingBlock } from '../../components/loading';
import BatchRepo from '../../repository/batch';
import BatchStats from './components/stats';
import { TransactionRepo } from '../../repository/transaction';
import TransactionTable from '../transaction/components/TransactionTable';
import { ItemList } from './components/list';
import { csvExportFunctions } from './csvExportFunctions';
import { transactionTypes } from '../../helpers/constants';

const { Title } = Typography;

const exportBatch = (batch, items, transactions) => {
  const { type } = batch;
  const { exportable, options, headers } = csvExportFunctions[type || 'internal'](items, transactions);
  let sheet;
  if (headers) {
    sheet = XLSX.utils.aoa_to_sheet(headers);
    XLSX.utils.sheet_add_json(sheet, exportable, options);
  } else {
    sheet = XLSX.utils.json_to_sheet(exportable, options);
  }
  const file = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(file, sheet, 'Sheet 1');
  XLSX.writeFile(file, `${batch.description}.xlsx`);
};

const isExternal = ({ dest_url }) => !dest_url;

const BatchDetail = (props) => {
  const { business, account, t } = props;
  const { batchID } = props.match.params;
  const [loading, setLoading] = useState(true);
  const [batch, setBatch] = useState({});
  const [batchItems, setBatchItems] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const loadBatchItems = useCallback(async (itemBatchID) => {
    try {
      const { data: { items } } = await BatchRepo.fetchItems({ business, account, batchID: itemBatchID });
      setBatchItems(items.map((item) => ({ ...item, amount: Number.parseInt(item.amount, 10) })));
    } catch (error) {
      console.error('ERROR: BatchDetail.loadBatchItems()', error);
      message.error(t('error.loading_batch_items'));
    } finally {
      setLoading(false);
    }
  }, [business, account, t]);

  const loadTransactions = useCallback(async (transactionAccount, transactionBatch) => {
    try {
      const snapshot = await TransactionRepo.fetchCollectionByBatch(transactionAccount, transactionBatch);
      let data = [];
      snapshot.forEach((d) => {
        data = [...data, d.data()];
      });
      setTransactions(data);
    } catch (error) {
      console.error('ERROR: BatchDetail: TransactionRepo.fetchCollectionByBatch()', error);
    }
  }, [setTransactions]);

  const loadBatch = useCallback(async (itemBatchID) => {
    try {
      const { data } = await BatchRepo.fetch({ business, account, batchID: itemBatchID });
      setBatch(data);
      loadTransactions(account, data);
    } catch (error) {
      console.error('ERROR: BatchDetail: BatchRepo.fetch()', error);
      message.error(t('error.loading_batch'));
    } finally {
      loadBatchItems(batchID);
    }
  }, [business, account, loadTransactions, t, loadBatchItems, batchID]);

  useEffect(() => {
    loadBatch(batchID);
  }, [loadBatch, batchID]);

  if (loading) {
    return <LoadingBlock />;
  }

  const type = (batchItems.length && isExternal(batchItems[0])) ? transactionTypes.external : transactionTypes.internal;
  return (
    <>
      <Title level={3}>{batch.description}</Title>

      <BatchStats items={batchItems} />
      <Tabs
        defaultActiveKey="1"
        style={{ textAlign: 'center' }}
        tabBarExtraContent={[
          <Button
            icon="download"
            key="export-batch"
            onClick={() => exportBatch(batch, batchItems, transactions)}
          >
            {t('export')}
          </Button>,
        ]}
      >
        <Tabs.TabPane tab={t('summary')} key="1">
          <ItemList transactions={batchItems} type={type} />
        </Tabs.TabPane>
        <Tabs.TabPane tab={t('transactions')} key="2">
          <TransactionTable static transactions={transactions} />
        </Tabs.TabPane>
      </Tabs>
    </>
  );
};

BatchDetail.propTypes = {
  account: PropTypes.object,
  business: PropTypes.object,
  match: PropTypes.shape({
    params: PropTypes.object,
  }),
  t: PropTypes.func,
};

export default subscribe(withTranslation()(BatchDetail), ['business', 'account']);
